<script setup lang="ts">
defineProps<{ loading?: boolean; template?: 'list' | 'split' | 'map' }>()
const emits = defineEmits<{ 'clear-refinements': [] }>()
defineSlots<{ default(): any }>()

const localePath = useLocalePath()
const { locale, t } = useI18n()
const { trackSuggestion } = useSearchTracking()
const {
  allDestinations,
  fetchAllDestinations,
  setData,
  setSubmittedSearch,
  submittedSearch,
  unknownDestination,
} = useSearchInput()

const buttonClicked = ref(false)

const destinations = computed(() =>
  (unknownDestination.value?.country
    ? allDestinations.value.filter(
        (d) => d.parent_id === unknownDestination.value?.country?.id,
      )
    : allDestinations.value.filter((d) => d.is_country)
  ).sort((a, b) => b.houses_count - a.houses_count),
)

const handleClickDestination = (desti: {
  id: number
  clusterizedName: string
  isCountry: boolean
}) => {
  trackSuggestion(desti.isCountry ? 'country' : 'region')
  setData('location', {
    id: desti.id.toString(),
    name: desti.clusterizedName,
    type: 'destination',
  })
  setSubmittedSearch(true)
}

const clearRefinements = () => {
  emits('clear-refinements')
}

onMounted(async () => {
  if (unknownDestination.value?.queryName && !allDestinations.value.length)
    await fetchAllDestinations()
})
</script>

<template>
  <!-- Because the router loads the route AFTER the destination state is updated, this prevent "blinking" -->
  <div v-if="submittedSearch || loading" class="min-h-svh w-full" />
  <div v-else>
    <div v-if="unknownDestination" class="mt-6 px-4 sm:max-w-[1080px] sm:px-0">
      <I18nT class="mb-1 font-medium" keypath="unavailableArea.label" tag="p">
        <template #bold>
          <strong>
            {{ t('unavailableArea.queryName', unknownDestination) }}
          </strong>
        </template>
      </I18nT>

      <p class="mb-0 text-sm">
        {{ t('unavailableArea.subtext') }}
      </p>

      <h3 class="mb-4 mt-10 text-xl font-bold sm:mb-5">
        <template v-if="unknownDestination.country">
          {{
            t('ourDestinationsIn', {
              country: unknownDestination.country.name[locale],
            })
          }}
        </template>
        <template v-else>
          {{ t('ourDestinations') }}
        </template>
      </h3>

      <DestinationGrid
        :class="{ 'md:grid-cols-5 md:gap-x-5': template === 'list' }"
        :destinations="destinations"
        :img-attrs="{ height: 100, width: 180 }"
        @click-destination="
          handleClickDestination({
            id: $event.id,
            clusterizedName: $event.clusterized_name[locale],
            isCountry: $event.is_country,
          })
        "
      />

      <SearchScheduleACall
        class="mt-10 w-full rounded bg-primary-100 p-5"
        orientation="horizontal"
        with-image
      />
    </div>

    <div v-else class="lc-container search-no-results">
      <h5
        class="text-secondary-600 mx-auto mb-4 mt-8 max-w-[310px] text-center text-2xl tablet:max-w-full"
      >
        {{ $t('search.noResultsTitle') }}
      </h5>

      <p class="mx-auto mb-4 max-w-[510px] text-center">
        {{ $t('search.noResultsText') }}
      </p>

      <div class="text-center">
        <BaseButton
          class="!px-3 !py-2 !text-md"
          color="secondary"
          @click="clearRefinements"
        >
          {{ $t('search.resetFilters') }}
        </BaseButton>
      </div>

      <div
        class="mx-auto mt-9 flex flex-col items-center text-sm tablet:flex-row lg:h-14 lg:w-1/2"
      >
        <BaseSlidingInputContainer
          :button-text="
            buttonClicked ? $t('global.validate') : $t('global.ctaCallMe')
          "
          :no-icon="buttonClicked"
          :notes="localePath('search')"
          class="flex-1"
          section="no results"
          @showInput="(value: boolean) => (buttonClicked = value)"
        />
        <BaseButton
          :to="localePath('contact')"
          color="black"
          class="mt-8 flex-1 justify-center tablet:mt-0"
          has-icon
          variant="link"
        >
          <BaseIcon name="mail" />
          <span class="ml-3">{{ $t('global.contactUs') }}</span>
        </BaseButton>
      </div>
    </div>

    <slot />
  </div>
</template>

<style scoped>
.search-no-results :deep(.sliding-input) {
  @apply mt-0 items-center justify-center;
}
</style>

<i18n lang="yaml">
en:
  ourDestinations: Our destinations
  ourDestinationsIn: 'Our destinations: {country}'
  unavailableArea:
    label: "{bold} We're not available in this area yet."
    queryName: '{queryName}:'
    subtext: Discover more destinations just as exceptional.
fr:
  ourDestinations: Nos destinations
  ourDestinationsIn: 'Nos destinations : {country}'
  unavailableArea:
    label: '{bold} Nous ne sommes pas encore présents par ici.'
    queryName: '{queryName} :'
    subtext: Découvrez d'autres destinations tout aussi exceptionnelles.
</i18n>
